import {generateClient, GraphQLResult} from 'aws-amplify/api';
import {
  signIn as signInAmplify,
  signOut as signOutAmplify,
  signUp as signUpAmplify,
  SignUpOutput,
  SignInOutput,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { UserService } from '@PikaDoApp/npm-pikado-logic';
import {validatePartnerAuthUser} from '../../graphql/mutations';
import {AuthenticatorResponse} from '../../graphql/API';
import {
  getPartnerCognitoId,
} from '../../redux/actions/user';
const client = generateClient();
const userService = new UserService();
export interface IAuthRepository {
  signIn(userName: string, password: string): Promise<SignInOutput | any>;
  signOut(): Promise<any>;
  signUp(email: string, password: string): Promise<SignUpOutput | Error>;
  validatePartnerAuth(
    partnerAppName: string,
    teacherId: string,
  ): Promise<AuthenticatorResponse>;

  getCurrentUser(): Promise<any>;
}

export class AuthRepository implements IAuthRepository {
  async getCurrentUser(): Promise<any> {
    console.log('ENTER_AUTH_REPOSITORY');

    try {
      let cognitoUserId: string;

      // Get cognito user ID from partner auth or from current auth session
      if (getPartnerCognitoId()) {
        cognitoUserId = getPartnerCognitoId();
      } else {
        const session = await fetchAuthSession();
        cognitoUserId = session.userSub || '';
      }

      console.log('Fetching user by cognitoUserId:', cognitoUserId);

      // Use the new UserService API
      const apiResponse = await userService.getUserByCognitoUserId(cognitoUserId);

      console.log('getUserByCognitoUserId response:', JSON.stringify(apiResponse, null, 2));

      // Fetch classes if school profile exists
      let classesData = [];
      if (apiResponse.schoolProfile?._id) {
        try {
          console.log('Fetching classes for schoolId:', apiResponse.schoolProfile._id);
          const classesResponse = await userService.getClassSchoolsBySchoolId(apiResponse.schoolProfile._id);
          console.log('getClassSchoolsBySchoolId response:', JSON.stringify(classesResponse, null, 2));

          // Transform classes to match the expected structure
          classesData = classesResponse.map((classItem: any) => ({
            id: classItem._id,
            name: classItem.name,
            nameArabic: classItem.nameArabic,
            grade: classItem.grade,
            createdAt: classItem.createdAt,
            updatedAt: classItem.updatedAt,
            __typename: 'Classschool',
          }));
        } catch (classError) {
          console.error('Error fetching classes:', classError);
          // Continue with empty classes if fetch fails
        }
      }

      // Transform the new API response to match the old structure expected by Redux
      const transformedUser = {
        id: apiResponse._id,
        cognitoUserId: apiResponse.cognitoUserId,
        whatsappNumber: apiResponse.whatsappNumber,
        country: apiResponse.country,
        timeZone: apiResponse.timeZone,
        createdAt: apiResponse.createdAt,
        updatedAt: apiResponse.updatedAt,

        // Map school profile
        school: apiResponse.schoolProfile ? {
          id: apiResponse.schoolProfile._id,
          name: apiResponse.schoolProfile.name,
          nameArabic: apiResponse.schoolProfile.nameArabic,
          createdAt: apiResponse.schoolProfile.createdAt,
          updatedAt: apiResponse.schoolProfile.updatedAt,
          promo: null, // Not in new API response
          classesschool: classesData,
          __typename: 'School',
        } : null,

        // Map teacher profile
        teacher: apiResponse.teacherProfile ? {
          id: apiResponse.teacherProfile._id,
          name: apiResponse.teacherProfile.name,
          gender: apiResponse.teacherProfile.gender,
          profilePicture: apiResponse.teacherProfile.profilePictureUrl ? {
            fileUri: apiResponse.teacherProfile.profilePictureUrl,
            thumbnailUri: apiResponse.teacherProfile.profilePictureUrl,
          } : null,
          classesschool: classesData,
          school: apiResponse.schoolProfile ? {
            id: apiResponse.schoolProfile._id,
            name: apiResponse.schoolProfile.name,
            nameArabic: apiResponse.schoolProfile.nameArabic,
            createdAt: apiResponse.schoolProfile.createdAt,
            updatedAt: apiResponse.schoolProfile.updatedAt,
            promo: null,
            __typename: 'School',
          } : null,
          __typename: 'Teacher',
        } : null,
      };

      console.log('Transformed user:', JSON.stringify(transformedUser, null, 2));

      return transformedUser;
    } catch (error) {
      console.error('Error in getCurrentUser:', error);
      throw error;
    }
  }

  async signIn(
    userName: string,
    password: string,
  ): Promise<SignInOutput | any> {
    return await signInAmplify({
      username: userName,
      password: password,
      options: {
        typeLogin: 'Schools',
      },
    });
  }

  async validatePartnerAuth(
    partnerAppName: string,
    teacherId: string,
  ): Promise<AuthenticatorResponse> {
    let token: GraphQLResult<any>;
    token = await client.graphql({
      query: validatePartnerAuthUser,
      variables: {
        input: {
          partnerId: teacherId,
          partnerAppName,
        },
      },
      authToken: 'partner_auth-' + partnerAppName,
    });
    return token.data.validatePartnerAuthUser;
  }

  async signOut(): Promise<any> {
    return await signOutAmplify();
  }

  async signUp(
    userName: string,
    password: string,
  ): Promise<SignUpOutput | Error> {
    return await signUpAmplify({
      username: userName,
      password: password,
    });
  }
}
